import React from "react";
import { MoveLeft } from "lucide-react";
import { OrderItem } from "../../context/OrderContext";
import { MenuItem, AppliedDeal } from "./pos.types";
import { useAuth } from "../../context/AuthContext";

export interface CartPanelProps {
  isOpen: boolean;
  cart: OrderItem[];
  menuItems: MenuItem[];
  appliedDeals: AppliedDeal[];
  subtotal: number;
  totalDealDiscount: number;
  discountAmount: number;
  grandTotal: number;
  loading: boolean;
  onClose: () => void;
  onClearCart: () => void;
  onUpdateQuantity: (cartId: string, qty: number) => void;
  onRemoveFromCart: (cartId: string) => void;
  onRemoveDeal: (dealId: string) => void;
  onCheckout: () => void;
  onEditCartItem: (cartItem: OrderItem) => void;
  onCustomizeItem: (item: MenuItem) => void;
  onAddDirectItem: (item: MenuItem) => void;
  getDisplayAddedOptions: (cartItem: any) => { label: string; qty: number }[];
  itemHasCustomizations: (item: MenuItem) => boolean;
}

const CartPanel: React.FC<CartPanelProps> = ({
  isOpen,
  cart,
  menuItems,
  appliedDeals,
  subtotal,
  totalDealDiscount,
  discountAmount,
  grandTotal,
  loading,
  onClose,
  onClearCart,
  onUpdateQuantity,
  onRemoveFromCart,
  onRemoveDeal,
  onCheckout,
  onEditCartItem,
  onCustomizeItem,
  onAddDirectItem,
  getDisplayAddedOptions,
  itemHasCustomizations,
}) => {
  const { currencySymbol } = useAuth();
  const dealIds = Array.from(
    new Set(
      cart
        .filter((item) => item.isPartOfDeal)
        .map((item) => item.dealId),
    ),
  ).filter((id): id is string => id !== undefined);

  const regularItems = cart.filter((item) => !item.isPartOfDeal);

  const cartCategories = new Set(
    cart
      .map((item) => {
        const menuItem = menuItems.find((m) => m._id === item.itemId);
        return menuItem?.category?.toLowerCase();
      })
      .filter(Boolean),
  );
  const cartItemIds = new Set(cart.map((item) => item.itemId));

  const hasMainItems =
    cartCategories.has("pizza") ||
    cartCategories.has("pasta") ||
    cartCategories.has("burgers") ||
    cartCategories.has("sandwiches") ||
    cartCategories.has("main course");
  const hasDrinks =
    cartCategories.has("drinks") || cartCategories.has("beverages");
  const hasDesserts =
    cartCategories.has("desserts") || cartCategories.has("dessert");

  const recommendations: MenuItem[] = [];
  if (hasMainItems && !hasDrinks) {
    const drinks = menuItems.filter(
      (item) =>
        (item.category?.toLowerCase() === "drinks" ||
          item.category?.toLowerCase() === "beverages") &&
        !cartItemIds.has(item._id),
    );
    recommendations.push(...drinks.slice(0, 3));
  }
  if (hasMainItems && !hasDesserts) {
    const desserts = menuItems.filter(
      (item) =>
        (item.category?.toLowerCase() === "desserts" ||
          item.category?.toLowerCase() === "dessert") &&
        !cartItemIds.has(item._id),
    );
    recommendations.push(...desserts.slice(0, 2));
  }
  if (!hasMainItems) {
    const otherItems = menuItems.filter(
      (item) =>
        !cartItemIds.has(item._id) &&
        !cartCategories.has(item.category?.toLowerCase()),
    );
    recommendations.push(...otherItems.slice(0, 5));
  }
  if (recommendations.length < 6) {
    const additionalItems = menuItems.filter(
      (item) =>
        !cartItemIds.has(item._id) &&
        !recommendations.some((r) => r._id === item._id) &&
        (item.category?.toLowerCase() === "sides" ||
          item.category?.toLowerCase() === "appetizers" ||
          item.category?.toLowerCase() === "drinks" ||
          item.category?.toLowerCase() === "beverages" ||
          item.category?.toLowerCase() === "desserts" ||
          item.category?.toLowerCase() === "dessert"),
    );
    recommendations.push(
      ...additionalItems.slice(0, 6 - recommendations.length),
    );
  }

  return (
    <div
      id="cart"
      role="dialog"
      aria-modal="true"
      aria-label="Shopping cart"
      className={`
  w-[350px] min-w-[300px] border-l-[1px] border-black/5 bg-white flex flex-col justify-start
   h-full
  fixed top-0 right-0 transition-transform duration-300 z-[99] dm-cart-drawer
  ${isOpen ? "translate-x-0" : "translate-x-full"}
`}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-2 py-[18px] border-b border-gray-200">
          <MoveLeft
            onClick={onClose}
            className="block sm:hidden cursor-pointer"
          />
          <span className="font-bold text-md">Your Cart</span>
          <button
            className="text-sm font-semibold disabled:opacity-30"
            disabled={cart.length === 0}
            onClick={onClearCart}
          >
            Clear All
          </button>
        </div>

        <div className="cart-body px-2 py-3 no-scrollbar">
          {cart.length === 0 ? (
            <div className="flex items-center justify-center h-full font-medium">
              Your cart is empty
            </div>
          ) : (
            <div className="space-y-2">
              {/* Regular Items */}
              {regularItems.map((cartItem) => (
                <div
                  className="flex items-center justify-start bg-white p-2 rounded-xl border-[1px] border-black/5 w-full dm-cart-item"
                  key={cartItem.cartId}
                  onClick={(e) => {
                    // if (
                    //   cartItem.customOptions &&
                    //   cartItem.customOptions.length > 0
                    // ) 
                    {
                      e.stopPropagation();
                      onEditCartItem(cartItem);
                    }
                  }}
                >
                  <div className="flex justify-start gap-3 w-full">
                    <img
                      src={cartItem.imageUrl || "/placeholder.png"}
                      alt={cartItem.name}
                      className="w-20 h-20 object-contain rounded-md border border-black-5"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          "/placeholder.png";
                      }}
                    />
                    <div className="flex flex-col w-full">
                      <div className="flex text-sm font-semibold justify-between w-full">
                        <span className="text-black text-sm break-words whitespace-normal max-w-[160px] line-clamp-3">
                          {cartItem.name}
                        </span>
                      </div>

                        {((cartItem.customOptions &&
                        cartItem.customOptions.length > 0) ||
                        cartItem.requestedCustomization) && (
                          <div className="mt-1 text-xs text-gray-600">
                            <div className="pr-2">
                              <span className="font-medium">
                                {getDisplayAddedOptions(cartItem).length > 0 && (
                                  <>
                                    Added:{" "}
                                    {getDisplayAddedOptions(cartItem)
                                      .map(
                                        (opt) =>
                                          `${opt.label}${opt.qty > 1 ? ` ×${opt.qty}` : ""}`,
                                      )
                                      .join(", ")}
                                  </>
                                )}
                               {(cartItem.customOptions?.filter(
                                  (opt) => opt.qty < 0,
                                 ).length ?? 0) > 0 && (
                                  <>
                                    {getDisplayAddedOptions(cartItem).length >
                                      0 && " | "}
                                    Removed:{" "}
                                       {(cartItem.customOptions || [])
                                      .filter((opt) => opt.qty < 0)
                                      .map(
                                        (opt) =>
                                          `${opt.label}${Math.abs(opt.qty) > 1 ? ` ×${Math.abs(opt.qty)}` : ""}`,
                                      )
                                      .join(", ")}
                                  </>
                                )}
                                {cartItem.requestedCustomization && (
                                  <span className="text-xs">
                                      {(getDisplayAddedOptions(cartItem).length >
                                      0 ||
                                      (cartItem.customOptions?.filter(
                                        (opt) => opt.qty < 0,
                                      ).length ?? 0) > 0) &&
                                      "| "}
                                    Special requests:{" "}
                                    {cartItem.requestedCustomization}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        )}

                      <div className="inline-flex items-center justify-between mt-2 ">
                        <span className="text-black text-sm font-bold">
                          {currencySymbol}{Number(cartItem.price).toFixed(2)}
                        </span>
                        <div className="flex dm-white-btn items-center border border-black/10 rounded-xl">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cartItem.quantity === 1) {
                                onRemoveFromCart(cartItem.cartId);
                              } else {
                                onUpdateQuantity(
                                  cartItem.cartId,
                                  cartItem.quantity - 1,
                                );
                              }
                            }}
                            className="w-8 h-8 rounded-md font-bold flex items-center justify-center text-black "
                          >
                            -
                          </button>
                          <div className="flex items-center justify-center w-6 h-5 gap-0">
                            <span className="font-medium text-black">
                              {String(cartItem.quantity)}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onUpdateQuantity(
                                cartItem.cartId,
                                cartItem.quantity + 1,
                              );
                            }}
                            className="w-8 h-8 rounded-md font-bold flex items-center justify-center text-black"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Deal Items Grouped */}
              {dealIds.map((dealId) => {
                const dealItems = cart.filter(
                  (item) => item.dealId === dealId,
                );
                const deal = appliedDeals.find((d) => d.dealId === dealId);

                if (!dealItems.length) return null;

                return (
                  <div
                    key={dealId}
                    className="p-3 rounded-lg border-2 border-black/50"
                  >
                    <div className="flex items-center justify-between mb-2 pb-2 border-b-2 border-black/50">
                      <div className="flex items-center gap-2">
                        <p className="m-0 font-bold text-sm text-gray-900">
                          {deal?.name || "Deal"}
                        </p>
                      </div>
                      <button
                        onClick={() => dealId && onRemoveDeal(dealId)}
                        className="font-bold text-sm"
                      >
                        x
                      </button>
                    </div>

                    <div className="space-y-2">
                      {dealItems.map((cartItem) => {
                        const isFreeItem =
                          deal?.dealType === "buy_x_get_y" &&
                          cartItem.price === 0;

                        return (
                          <div
                            key={cartItem.cartId}
                            className="flex items-center gap-2  p-2 rounded-md"
                          >
                            <img
                              src={cartItem.imageUrl || "/placeholder.png"}
                              alt={cartItem.name}
                              className="w-10 h-10 object-contain rounded-md border border-gray-200"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">
                                  {cartItem.name}
                                </span>
                                {isFreeItem && (
                                  <span className="text-xs text-black px-2 py-0.5 rounded-full font-bold">
                                    FREE
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-black">
                                <span>Qty: {cartItem.quantity}</span>
                                {!isFreeItem && (
                                  <>
                                    {cartItem.originalPrice &&
                                      cartItem.originalPrice !==
                                        cartItem.price && (
                                        <span className="line-through text-black">
                                          {currencySymbol}
                                          {(
                                            cartItem.originalPrice *
                                            cartItem.quantity
                                          ).toFixed(2)}
                                        </span>
                                      )}
                                    <span className="font-semibold text-black">
                                      {currencySymbol}
                                      {(
                                        cartItem.price * cartItem.quantity
                                      ).toFixed(2)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recommended for you Section */}
        {cart.length > 0 && recommendations.length > 0 && (
          <div className="px-4 py-3 border-t border-black/20 bg-white">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm text-gray-900">
                Recommended for you
              </h3>
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-thin items-stretch">
              {recommendations.slice(0, 6).map((item) => {
                const isInCart = cart.some(
                  (cartItem) => cartItem.itemId === item._id,
                );
                const hasCustomizations = itemHasCustomizations(item);

                return (
                  <div
                    key={item._id}
                    className="flex-shrink-0 w-[120px] bg-white rounded-lg border border-black/20 p-2 mb-2 flex flex-col"
                  >
                    <div className="w-full h-20 mb-2 overflow-hidden rounded-md">
                      <img
                        src={item.imageUrl || "/placeholder.png"}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-md mb-2"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "/placeholder.png";
                        }}
                      />
                    </div>
                    <p className="text-xs font-medium text-gray-900 line-clamp-3 mb-1 h-12 text-left">
                      {item.name}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-sm font-bold text-gray-900">
                        {currencySymbol}{Number(item.price).toFixed(2)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isInCart) return;
                          if (hasCustomizations) {
                            onCustomizeItem(item);
                          } else {
                            onAddDirectItem(item);
                          }
                        }}
                        disabled={isInCart}
                        className={`text-xs font-bold px-2 py-1 border border-main/30 rounded-md transition-all ${
                          isInCart
                            ? "bg-main/20 text-black cursor-not-allowed"
                            : "bg-white text-black dm-white-btn dm-no-hover"
                        }`}
                      >
                        {isInCart ? "✓" : "+"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary + Checkout */}
        <div className="cart-footer p-4 border-t border-black/5 flex flex-col gap-1">
          <div className="flex justify-between text-sm font-semibold  ">
            <span>Cart:</span>
            <span>{currencySymbol}{subtotal.toFixed(2)}</span>
          </div>
          {totalDealDiscount > 0 && (
            <div className="flex justify-between text-black text-sm font-medium">
              <span>Deal Discount:</span>
              <span>-{currencySymbol}{totalDealDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-medium  ">
            <span>Discount Amount:</span>
            <span>-{currencySymbol}{discountAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold  ">
            <span>Total:</span>
            <span>{currencySymbol}{grandTotal.toFixed(2)}</span>
          </div>
          <button
            onClick={onCheckout}
            className="bg-main/90 border-0 rounded-[10px] font-semibold cursor-pointer text-sm w-full p-2 text-white dm-checkout-btn"
            disabled={loading}
          >
            {loading ? "Processing..." : "Checkout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPanel;
